import { Alert, AlertOverview, NotificationChannel, RoutingRule, MaintenanceWindow, CreateAlertRequest, UpdateAlertRequest, AlertFilters } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

interface AlertsApiResponse {
  alerts: Alert[];
  overview: AlertOverview;
  notificationChannels: NotificationChannel[];
  routingRules: RoutingRule[];
  maintenanceWindows: MaintenanceWindow[];
  lastUpdated: string;
}

class AlertsService {
  private ongoingRequests = new Map<string, Promise<any>>();
  
  private getAuthHeaders() {
    const token = localStorage.getItem('auth_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  async getAllAlerts(filters?: AlertFilters): Promise<AlertsApiResponse> {
    // First, let's try a simple fetch without all the complexity
    console.log('=== DEBUG: Starting getAllAlerts ===');
    console.log('API_BASE_URL:', API_BASE_URL);
    console.log('Filters:', filters);
    const queryParams = new URLSearchParams();
    
    if (filters?.accountId?.length) {
      queryParams.append('accountId', filters.accountId.join(','));
    }
    if (filters?.region?.length) {
      queryParams.append('region', filters.region.join(','));
    }
    if (filters?.service?.length) {
      queryParams.append('service', filters.service.join(','));
    }
    if (filters?.severity?.length) {
      queryParams.append('severity', filters.severity.join(','));
    }
    if (filters?.state?.length) {
      queryParams.append('state', filters.state.join(','));
    }
    if (filters?.status?.length) {
      queryParams.append('status', filters.status.join(','));
    }
    if (filters?.search) {
      queryParams.append('search', filters.search);
    }

    const url = `${API_BASE_URL}/api/alerts${queryParams.toString() ? `?${queryParams}` : ''}`;
    console.log('=== DEBUG: Constructed URL:', url);
    
    // Check if we already have an ongoing request for this URL
    if (this.ongoingRequests.has(url)) {
      console.log('Alerts Service: Using existing request for', url);
      return this.ongoingRequests.get(url)!;
    }

    const token = localStorage.getItem('auth_token');
    
    console.log('Alerts Service: Making new request', { 
      url, 
      hasToken: !!token,
      tokenLength: token?.length,
      timestamp: new Date().toISOString(),
      tokenPreview: token?.substring(0, 50) + '...',
      apiBaseUrl: API_BASE_URL,
      fullToken: token // Full token for debugging
    });
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
    
    const requestPromise = (async () => {
      try {
        console.log('Alerts Service: About to make fetch request', {
          url,
          method: 'GET',
          headers: {
            'Authorization': token ? `Bearer ${token?.substring(0, 20)}...` : 'NO TOKEN',
            'Content-Type': 'application/json'
          }
        });

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json'
          },
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);

        console.log('Alerts Service: Response received', {
          status: response.status,
          ok: response.ok
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Alerts Service: Error response', errorText);
          throw new Error(`Failed to fetch alerts: ${response.status} ${response.statusText}`);
        }

        return response.json();
      } catch (error: any) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          console.error('Alerts Service: Request timeout after 30 seconds');
          throw new Error('Request timeout - server may be unreachable or overloaded');
        }
        if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
          console.error('Alerts Service: Network error - cannot reach server');
          throw new Error('Cannot reach server - check network connection');
        }
        console.error('Alerts Service: Unexpected error', error);
        throw new Error(`Unexpected error: ${error.message || 'Unknown error'}`);
      } finally {
        // Clean up the request from the map
        this.ongoingRequests.delete(url);
      }
    })();

    // Store the request promise
    this.ongoingRequests.set(url, requestPromise);
    
    return requestPromise;
  }

  async getAWSCloudWatchAlerts(accountId?: string, region?: string): Promise<Alert[]> {
    const queryParams = new URLSearchParams();
    if (accountId) queryParams.append('accountId', accountId);
    if (region) queryParams.append('region', region);

    const url = `${API_BASE_URL}/api/alerts/aws/cloudwatch${queryParams.toString() ? `?${queryParams}` : ''}`;
    
    const response = await fetch(url, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch AWS CloudWatch alerts: ${response.status}`);
    }

    const data = await response.json();
    return data.alerts || [];
  }

  async getGCPMonitoringAlerts(accountId?: string, region?: string): Promise<Alert[]> {
    const queryParams = new URLSearchParams();
    if (accountId) queryParams.append('accountId', accountId);
    if (region) queryParams.append('region', region);

    const url = `${API_BASE_URL}/api/alerts/gcp/monitoring${queryParams.toString() ? `?${queryParams}` : ''}`;
    
    const response = await fetch(url, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch GCP monitoring alerts: ${response.status}`);
    }

    const data = await response.json();
    return data.alerts || [];
  }

  async createAlert(alertData: CreateAlertRequest): Promise<Alert> {
    const response = await fetch(`${API_BASE_URL}/api/alerts`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(alertData)
    });

    if (!response.ok) {
      throw new Error(`Failed to create alert: ${response.status}`);
    }

    return response.json();
  }

  async updateAlert(alertData: UpdateAlertRequest): Promise<Alert> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/${alertData.id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(alertData)
    });

    if (!response.ok) {
      throw new Error(`Failed to update alert: ${response.status}`);
    }

    return response.json();
  }

  async deleteAlert(alertId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/${alertId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to delete alert: ${response.status}`);
    }
  }

  async acknowledgeAlert(alertId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/${alertId}/acknowledge`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to acknowledge alert: ${response.status}`);
    }
  }

  async suppressAlert(alertId: string, duration: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/${alertId}/suppress`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ duration })
    });

    if (!response.ok) {
      throw new Error(`Failed to suppress alert: ${response.status}`);
    }
  }

  // Notification Channels
  async getNotificationChannels(): Promise<NotificationChannel[]> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/channels`, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch notification channels: ${response.status}`);
    }

    const data = await response.json();
    return data.channels || [];
  }

  async createNotificationChannel(channelData: Omit<NotificationChannel, 'id' | 'createdAt' | 'updatedAt'>): Promise<NotificationChannel> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/channels`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(channelData)
    });

    if (!response.ok) {
      throw new Error(`Failed to create notification channel: ${response.status}`);
    }

    return response.json();
  }

  async updateNotificationChannel(channelData: NotificationChannel): Promise<NotificationChannel> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/channels/${channelData.id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(channelData)
    });

    if (!response.ok) {
      throw new Error(`Failed to update notification channel: ${response.status}`);
    }

    return response.json();
  }

  async deleteNotificationChannel(channelId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/channels/${channelId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to delete notification channel: ${response.status}`);
    }
  }

  async testNotificationChannel(channelId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/channels/${channelId}/test`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to test notification channel: ${response.status}`);
    }
  }

  // Routing Rules
  async getRoutingRules(): Promise<RoutingRule[]> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/routing-rules`, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch routing rules: ${response.status}`);
    }

    const data = await response.json();
    return data.routingRules || [];
  }

  async createRoutingRule(ruleData: Omit<RoutingRule, 'id' | 'createdAt' | 'updatedAt'>): Promise<RoutingRule> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/routing-rules`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(ruleData)
    });

    if (!response.ok) {
      throw new Error(`Failed to create routing rule: ${response.status}`);
    }

    return response.json();
  }

  async updateRoutingRule(ruleData: RoutingRule): Promise<RoutingRule> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/routing-rules/${ruleData.id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(ruleData)
    });

    if (!response.ok) {
      throw new Error(`Failed to update routing rule: ${response.status}`);
    }

    return response.json();
  }

  async deleteRoutingRule(ruleId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/routing-rules/${ruleId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to delete routing rule: ${response.status}`);
    }
  }

  // Maintenance Windows
  async getMaintenanceWindows(): Promise<MaintenanceWindow[]> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/maintenance-windows`, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch maintenance windows: ${response.status}`);
    }

    const data = await response.json();
    return data.maintenanceWindows || [];
  }

  async createMaintenanceWindow(windowData: Omit<MaintenanceWindow, 'id' | 'createdAt' | 'updatedAt'>): Promise<MaintenanceWindow> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/maintenance-windows`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(windowData)
    });

    if (!response.ok) {
      throw new Error(`Failed to create maintenance window: ${response.status}`);
    }

    return response.json();
  }

  async updateMaintenanceWindow(windowData: MaintenanceWindow): Promise<MaintenanceWindow> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/maintenance-windows/${windowData.id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(windowData)
    });

    if (!response.ok) {
      throw new Error(`Failed to update maintenance window: ${response.status}`);
    }

    return response.json();
  }

  async deleteMaintenanceWindow(windowId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/maintenance-windows/${windowId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to delete maintenance window: ${response.status}`);
    }
  }

  // Metrics and Analytics
  async getAlertsMetrics(timeRange: '1h' | '24h' | '7d' | '30d' = '24h'): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/metrics?timeRange=${timeRange}`, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch alerts metrics: ${response.status}`);
    }

    return response.json();
  }

  // Live Updates (Server-Sent Events)
  createEventSource(): EventSource | null {
    const token = localStorage.getItem('auth_token');
    if (!token) return null;

    return new EventSource(`${API_BASE_URL}/api/alerts/events?token=${encodeURIComponent(token)}`);
  }
}

export const alertsService = new AlertsService();
export default alertsService;